import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

interface Check {
  path: string;
  expect: RegExp;
  label: string;
}

const CHECKS: Check[] = [
  { path: "/humans.txt",     expect: /^text\/plain/,             label: "humans.txt" },
  { path: "/llms.txt",       expect: /^text\/plain/,             label: "llms.txt" },
  { path: "/robots.txt",     expect: /^text\/plain/,             label: "robots.txt" },
  { path: "/sitemap.xml",    expect: /^(application|text)\/xml/, label: "sitemap.xml" },
  { path: "/sitemap-md.xml", expect: /^(application|text)\/xml/, label: "sitemap-md.xml (markdown mirrors)" },
];

interface Result {
  path: string;
  label: string;
  status: number | null;
  contentType: string;
  charset: string;
  firstLine: string;
  byteSize: number;
  headers: Record<string, string>;
  ok: boolean;
  error?: string;
}

function parseCharset(ct: string): string {
  const m = /charset=([^;]+)/i.exec(ct);
  return m ? m[1].trim() : "(unspecified)";
}

async function runCheck(c: Check): Promise<Result> {
  try {
    const res = await fetch(c.path, { cache: "no-store" });
    const ct = res.headers.get("content-type") ?? "";
    const text = await res.text();
    const firstLine = text.split(/\r?\n/, 1)[0] ?? "";
    const headers: Record<string, string> = {};
    res.headers.forEach((v, k) => { headers[k] = v; });
    return {
      path: c.path,
      label: c.label,
      status: res.status,
      contentType: ct,
      charset: parseCharset(ct),
      firstLine: firstLine.slice(0, 200),
      byteSize: new Blob([text]).size,
      headers,
      ok: res.status === 200 && c.expect.test(ct),
    };
  } catch (e) {
    return {
      path: c.path, label: c.label, status: null, contentType: "", charset: "",
      firstLine: "", byteSize: 0, headers: {}, ok: false, error: (e as Error).message,
    };
  }
}

interface SubmitResult {
  target: "google" | "bing" | "post-deploy";
  ok: boolean;
  status?: number;
  finishedAt: string;
  detail: unknown;
}

interface HistoryEntry {
  id: string;
  ranAt: string;
  results: Result[];
  allOk: boolean;
  failingCount: number;
}

interface PsiRun {
  strategy: "mobile" | "desktop";
  ok: boolean;
  status: number;
  fetchedAt: string;
  score: number | null;
  fcp: number | null;
  lcp: number | null;
  si: number | null;
  tbt: number | null;
  cls: number | null;
  error?: string;
}

interface PsiHistoryEntry {
  id: string;
  ranAt: string;
  url: string;
  runs: PsiRun[];
}

const HISTORY_KEY = "publish-check:history:v1";
const HISTORY_MAX = 20;
const PSI_HISTORY_KEY = "publish-check:psi-history:v1";
const PSI_HISTORY_MAX = 20;
const ADMIN_TOKEN_KEY = "publish-check:admin-token:v1";

function getAdminToken(): string {
  if (typeof window === "undefined") return "";
  // Tab-scoped sessionStorage instead of localStorage so the admin token
  // isn't persisted across sessions where third-party analytics scripts run.
  let token = window.sessionStorage.getItem(ADMIN_TOKEN_KEY) ?? "";
  if (!token) {
    token = window.prompt("Enter admin token (set as ADMIN_TOKEN secret in Lovable Cloud):") ?? "";
    if (token) window.sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
  }
  // Purge any legacy localStorage copy from previous versions.
  try { window.localStorage.removeItem(ADMIN_TOKEN_KEY); } catch { /* ignore */ }
  return token;
}

function adminHeaders(extra?: Record<string, string>): Record<string, string> {
  return { "x-admin-token": getAdminToken(), ...(extra ?? {}) };
}


function loadHistory(): HistoryEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    return raw ? (JSON.parse(raw) as HistoryEntry[]) : [];
  } catch {
    return [];
  }
}

function saveHistory(entries: HistoryEntry[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, HISTORY_MAX)));
  } catch {
    /* quota — ignore */
  }
}

function loadPsiHistory(): PsiHistoryEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(PSI_HISTORY_KEY);
    return raw ? (JSON.parse(raw) as PsiHistoryEntry[]) : [];
  } catch {
    return [];
  }
}

function savePsiHistory(entries: PsiHistoryEntry[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PSI_HISTORY_KEY, JSON.stringify(entries.slice(0, PSI_HISTORY_MAX)));
  } catch {
    /* quota — ignore */
  }
}


function diffSummary(curr: Result[], prev: Result[] | undefined): string | null {
  if (!prev || curr.length === 0) return null;
  const changes: string[] = [];
  for (const c of curr) {
    const p = prev.find((x) => x.path === c.path);
    if (!p) continue;
    if (p.status !== c.status) changes.push(`${c.path}: ${p.status ?? "—"}→${c.status ?? "—"}`);
    else if (p.contentType !== c.contentType) changes.push(`${c.path}: content-type changed`);
    else if (p.ok !== c.ok) changes.push(`${c.path}: ${p.ok ? "pass→fail" : "fail→pass"}`);
  }
  return changes.length === 0 ? "No changes vs previous run." : changes.join(" · ");
}

interface SitemapStatusReport {
  ok: boolean;
  checkedAt: string;
  totals: { submitted: number; indexed: number; errors: number; warnings: number };
  google: {
    site: string;
    sitemaps: Array<{
      path: string;
      ok: boolean;
      httpStatus: number;
      registered: boolean;
      lastSubmitted?: string;
      lastDownloaded?: string;
      warnings?: string;
      errors?: string;
      submitted?: string;
      indexed?: string;
      error?: string;
    }>;
  };
  bing: { ok: boolean; httpStatus: number; keyUrl: string; keyReachable: boolean; keyMatches: boolean; note: string };
}

function PublishCheck() {
  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [submitting, setSubmitting] = useState<"google" | "bing" | "post-deploy" | null>(null);
  const [submitResults, setSubmitResults] = useState<SubmitResult[]>([]);
  const [psiHistory, setPsiHistory] = useState<PsiHistoryEntry[]>([]);
  const [psiRunning, setPsiRunning] = useState(false);
  const [psiError, setPsiError] = useState<string | null>(null);
  const [sitemapStatus, setSitemapStatus] = useState<SitemapStatusReport | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const refreshStatus = async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const res = await fetch("/api/admin/sitemap-status", { cache: "no-store", headers: adminHeaders() });
      const json = (await res.json()) as SitemapStatusReport;
      setSitemapStatus(json);
    } catch (e) {
      setStatusError((e as Error).message);
    } finally {
      setStatusLoading(false);
    }
  };

  const runPostDeploy = async () => {
    setSubmitting("post-deploy");
    try {
      const res = await fetch("/api/admin/post-deploy-sitemaps", { method: "POST", headers: adminHeaders() });
      const detail = await res.json().catch(() => ({}));
      setSubmitResults((prev) => [
        {
          target: "post-deploy" as const,
          ok: res.ok && (detail as { ok?: boolean }).ok !== false,
          status: res.status,
          finishedAt: new Date().toISOString(),
          detail,
        },
        ...prev,
      ].slice(0, 10));
      if ((detail as { status?: SitemapStatusReport }).status) {
        setSitemapStatus((detail as { status: SitemapStatusReport }).status);
      } else {
        await refreshStatus();
      }
    } finally {
      setSubmitting(null);
    }
  };

  useEffect(() => {
    setHistory(loadHistory());
    setPsiHistory(loadPsiHistory());
    void refreshStatus();
  }, []);


  const run = async () => {
    setLoading(true);
    setResults([]);
    const out = await Promise.all(CHECKS.map(runCheck));
    setResults(out);
    setLoading(false);
    const entry: HistoryEntry = {
      id: crypto.randomUUID(),
      ranAt: new Date().toISOString(),
      results: out,
      allOk: out.every((r) => r.ok),
      failingCount: out.filter((r) => !r.ok).length,
    };
    const next = [entry, ...loadHistory()].slice(0, HISTORY_MAX);
    saveHistory(next);
    setHistory(next);
  };

  const resubmit = async (target: "google" | "bing") => {
    setSubmitting(target);
    try {
      const res = await fetch(`/api/admin/resubmit-${target}`, { method: "POST", headers: adminHeaders() });
      const detail = await res.json().catch(() => ({}));
      setSubmitResults((prev) => [
        {
          target,
          ok: res.ok && (detail as { ok?: boolean }).ok !== false,
          status: res.status,
          finishedAt: new Date().toISOString(),
          detail,
        },
        ...prev,
      ].slice(0, 10));
    } catch (e) {
      setSubmitResults((prev) => [
        { target, ok: false, finishedAt: new Date().toISOString(), detail: { error: (e as Error).message } },
        ...prev,
      ].slice(0, 10));
    } finally {
      setSubmitting(null);
    }
  };

  const runPsi = async () => {
    setPsiRunning(true);
    setPsiError(null);
    try {
      const res = await fetch("/api/admin/pagespeed", {
        method: "POST",
        headers: adminHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ url: "https://arifkhanglobal.com/" }),
      });

      const json = (await res.json()) as { ok: boolean; url: string; runs?: PsiRun[]; error?: string };
      if (!res.ok || !json.runs) {
        setPsiError(json.error ?? `HTTP ${res.status}`);
        return;
      }
      const entry: PsiHistoryEntry = {
        id: crypto.randomUUID(),
        ranAt: new Date().toISOString(),
        url: json.url,
        runs: json.runs,
      };
      const next = [entry, ...loadPsiHistory()].slice(0, PSI_HISTORY_MAX);
      savePsiHistory(next);
      setPsiHistory(next);
    } catch (e) {
      setPsiError((e as Error).message);
    } finally {
      setPsiRunning(false);
    }
  };

  const clearHistory = () => { saveHistory([]); setHistory([]); };
  const clearPsiHistory = () => { savePsiHistory([]); setPsiHistory([]); };

  useEffect(() => { run(); }, []);



  const allOk = results.length > 0 && results.every((r) => r.ok);
  const previous = history[0]?.results; // history[0] is prior run (current run pushed after)
  const diff = diffSummary(results, previous);

  return (
    <main className="min-h-screen bg-background px-4 py-12">
      <div className="mx-auto max-w-5xl">
        <h1 className="text-2xl font-semibold tracking-tight">Publish Verification</h1>
        <p className="mt-2 text-sm text-ink-soft">
          Status, content-type, charset, first line, and full response headers for every
          crawler-facing endpoint. Click a row to expand details.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            onClick={run}
            disabled={loading}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {loading ? "Checking…" : "Re-check"}
          </button>
          <button
            onClick={() => resubmit("google")}
            disabled={submitting !== null}
            className="rounded-full border border-border bg-surface px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {submitting === "google" ? "Submitting…" : "Resubmit to Google"}
          </button>
          <button
            onClick={() => resubmit("bing")}
            disabled={submitting !== null}
            className="rounded-full border border-border bg-surface px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {submitting === "bing" ? "Submitting…" : "Resubmit to Bing (IndexNow)"}
          </button>
          <button
            onClick={runPostDeploy}
            disabled={submitting !== null}
            className="rounded-full border border-primary bg-primary/10 px-4 py-2 text-sm font-medium text-primary disabled:opacity-50"
            title="Resubmits all sitemaps to Google + Bing, then fetches the live status report. Call this after each deploy."
          >
            {submitting === "post-deploy" ? "Running post-deploy…" : "Post-deploy: resubmit + status"}
          </button>
          <button
            onClick={runPsi}
            disabled={psiRunning}
            className="rounded-full border border-border bg-surface px-4 py-2 text-sm font-medium disabled:opacity-50"
            title="Runs Google PageSpeed Insights for the published homepage (mobile + desktop). ~60s."
          >
            {psiRunning ? "Running PageSpeed…" : "Run PageSpeed Insights"}
          </button>
          {results.length > 0 && (
            <span className={`text-sm font-medium ${allOk ? "text-emerald-600" : "text-red-600"}`}>
              {allOk ? "All checks passed" : `${results.filter((r) => !r.ok).length} failing`}
            </span>
          )}
        </div>

        {psiError && (
          <p className="mt-3 text-xs text-red-600">
            <span className="font-semibold uppercase tracking-widest">PageSpeed error:</span> {psiError}
          </p>
        )}

        <PsiPanel history={psiHistory} onClear={clearPsiHistory} />


        {diff && (
          <p className="mt-3 text-xs text-ink-soft">
            <span className="font-semibold uppercase tracking-widest">Diff vs previous:</span> {diff}
          </p>
        )}

        <SitemapStatusPanel
          status={sitemapStatus}
          loading={statusLoading}
          error={statusError}
          onRefresh={refreshStatus}
        />

        {submitResults.length > 0 && (
          <div className="mt-6 rounded-lg border border-border bg-surface/30 p-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-ink-soft">
              Sitemap submissions
            </div>
            <div className="space-y-2">
              {submitResults.map((s, i) => (
                <details key={i} className="text-xs">
                  <summary className="cursor-pointer">
                    <span className={s.ok ? "text-emerald-600" : "text-red-600"}>{s.ok ? "✓" : "✗"}</span>{" "}
                    <span className="font-mono">{s.target}</span> · HTTP {s.status ?? "—"} ·{" "}
                    {new Date(s.finishedAt).toLocaleTimeString()}
                  </summary>
                  <pre className="mt-2 whitespace-pre-wrap break-all rounded border border-border bg-background p-2 font-mono">
                    {JSON.stringify(s.detail, null, 2)}
                  </pre>
                </details>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-surface text-left text-xs uppercase tracking-widest text-ink-soft">
              <tr>
                <th className="px-4 py-3">Endpoint</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Content-Type</th>
                <th className="px-4 py-3">Charset</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3">Result</th>
              </tr>
            </thead>
            <tbody>
              {results.flatMap((r) => {
                const open = expanded === r.path;
                const rows = [
                  <tr
                    key={r.path}
                    className="border-t border-border cursor-pointer hover:bg-surface/50"
                    onClick={() => setExpanded(open ? null : r.path)}
                  >
                    <td className="px-4 py-3 font-mono text-xs">
                      <a href={r.path} target="_blank" rel="noreferrer" className="hover:text-primary" onClick={(e) => e.stopPropagation()}>{r.path}</a>
                      <div className="text-ink-soft">{r.label}</div>
                    </td>
                    <td className="px-4 py-3 font-mono">{r.status ?? "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs break-all">{r.contentType || r.error || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.charset || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.byteSize ? `${r.byteSize.toLocaleString()} B` : "—"}</td>
                    <td className="px-4 py-3">
                      <span className={r.ok ? "text-emerald-600" : "text-red-600"}>{r.ok ? "✓ Pass" : "✗ Fail"}</span>
                    </td>
                  </tr>,
                ];
                if (open) {
                  rows.push(
                    <tr key={r.path + "-detail"} className="border-t border-border bg-surface/30">
                      <td colSpan={6} className="px-4 py-4">
                        <div className="space-y-3 text-xs">
                          <div>
                            <div className="font-semibold uppercase tracking-widest text-ink-soft mb-1">First line</div>
                            <pre className="font-mono whitespace-pre-wrap break-all rounded bg-background p-2 border border-border">{r.firstLine || "(empty)"}</pre>
                          </div>
                          <div>
                            <div className="font-semibold uppercase tracking-widest text-ink-soft mb-1">Response headers</div>
                            <pre className="font-mono whitespace-pre-wrap break-all rounded bg-background p-2 border border-border">{Object.entries(r.headers).map(([k, v]) => k + ": " + v).join("\n") || "(none)"}</pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                }
                return rows;
              })}
              {results.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-ink-soft">No results yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {history.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-widest text-ink-soft">
                Run history ({history.length})
              </h2>
              <button onClick={clearHistory} className="text-xs text-ink-soft underline hover:text-foreground">
                Clear
              </button>
            </div>
            <div className="mt-3 overflow-hidden rounded-lg border border-border">
              <table className="w-full text-xs">
                <thead className="bg-surface text-left uppercase tracking-widest text-ink-soft">
                  <tr>
                    <th className="px-4 py-2">When</th>
                    <th className="px-4 py-2">Result</th>
                    <th className="px-4 py-2">Failing</th>
                    <th className="px-4 py-2">Per-endpoint status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-t border-border">
                      <td className="px-4 py-2 font-mono">{new Date(h.ranAt).toLocaleString()}</td>
                      <td className="px-4 py-2">
                        <span className={h.allOk ? "text-emerald-600" : "text-red-600"}>
                          {h.allOk ? "✓ Pass" : "✗ Fail"}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono">{h.failingCount}</td>
                      <td className="px-4 py-2 font-mono text-ink-soft">
                        {h.results.map((r) => `${r.path.replace(/^\//, "")}:${r.status ?? "—"}`).join(" · ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="mt-6 text-xs text-ink-soft">
          Open this page on <code className="font-mono">arifkhanglobal.com</code> after publishing to verify production.
          History is stored in your browser's localStorage (last {HISTORY_MAX} runs).
        </p>
      </div>
    </main>
  );
}

// ---- PageSpeed Insights panel ---------------------------------------------

type PsiMetricKey = "score" | "fcp" | "lcp" | "si" | "tbt" | "cls";

// Higher score = better, all others lower = better.
const PSI_DIRECTION: Record<PsiMetricKey, "higher" | "lower"> = {
  score: "higher", fcp: "lower", lcp: "lower", si: "lower", tbt: "lower", cls: "lower",
};

// Threshold below which a delta is considered noise (PSI is inherently noisy).
const PSI_NOISE: Record<PsiMetricKey, number> = {
  score: 2, fcp: 0.1, lcp: 0.15, si: 0.1, tbt: 20, cls: 0.005,
};

function fmt(metric: PsiMetricKey, v: number | null): string {
  if (v === null) return "—";
  if (metric === "score") return String(v);
  if (metric === "tbt") return `${v} ms`;
  if (metric === "cls") return v.toFixed(3);
  return `${v.toFixed(1)} s`;
}

function delta(metric: PsiMetricKey, curr: number | null, prev: number | null) {
  if (curr === null || prev === null) return { text: "", cls: "text-ink-soft" };
  const d = curr - prev;
  if (Math.abs(d) < PSI_NOISE[metric]) return { text: "≈", cls: "text-ink-soft" };
  const improved = PSI_DIRECTION[metric] === "higher" ? d > 0 : d < 0;
  const sign = d > 0 ? "+" : "";
  const fmted =
    metric === "score" ? `${sign}${Math.round(d)}` :
    metric === "tbt" ? `${sign}${Math.round(d)}ms` :
    metric === "cls" ? `${sign}${d.toFixed(3)}` :
    `${sign}${d.toFixed(2)}s`;
  return { text: fmted, cls: improved ? "text-emerald-600" : "text-red-600 font-semibold" };
}

function PsiPanel({ history, onClear }: { history: PsiHistoryEntry[]; onClear: () => void }) {
  if (history.length === 0) {
    return (
      <p className="mt-6 text-xs text-ink-soft">
        No PageSpeed runs yet. Click <span className="font-semibold">Run PageSpeed Insights</span> to
        measure the published homepage. Requires <code className="font-mono">PAGESPEED_API_KEY</code> in
        Project Settings → Secrets.
      </p>
    );
  }
  const metrics: PsiMetricKey[] = ["score", "fcp", "lcp", "si", "tbt", "cls"];
  return (
    <div className="mt-8">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-ink-soft">
          PageSpeed Insights ({history.length})
        </h2>
        <button onClick={onClear} className="text-xs text-ink-soft underline hover:text-foreground">
          Clear
        </button>
      </div>
      <div className="mt-3 overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-xs">
          <thead className="bg-surface text-left uppercase tracking-widest text-ink-soft">
            <tr>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">Device</th>
              <th className="px-3 py-2">Score</th>
              <th className="px-3 py-2">FCP</th>
              <th className="px-3 py-2">LCP</th>
              <th className="px-3 py-2">SI</th>
              <th className="px-3 py-2">TBT</th>
              <th className="px-3 py-2">CLS</th>
            </tr>
          </thead>
          <tbody>
            {history.flatMap((entry, entryIdx) =>
              entry.runs.map((run) => {
                // Find previous run for the SAME strategy to compare.
                const prevEntry = history
                  .slice(entryIdx + 1)
                  .find((e) => e.runs.some((r) => r.strategy === run.strategy));
                const prev = prevEntry?.runs.find((r) => r.strategy === run.strategy);
                return (
                  <tr key={entry.id + run.strategy} className="border-t border-border align-top">
                    <td className="px-3 py-2 font-mono whitespace-nowrap">
                      {new Date(entry.ranAt).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 font-mono">{run.strategy}</td>
                    {metrics.map((m) => {
                      const v = run[m];
                      const d = prev ? delta(m, v, prev[m]) : { text: "", cls: "" };
                      return (
                        <td key={m} className="px-3 py-2 font-mono">
                          <div>{fmt(m, v)}</div>
                          {d.text && <div className={`text-[10px] ${d.cls}`}>{d.text}</div>}
                        </td>
                      );
                    })}
                  </tr>
                );
              }),
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-ink-soft">
        Deltas compare against the previous run for the same device. Red = regression, green = improvement, ≈ = within noise.
      </p>
    </div>
  );
}

// ---- Sitemap status panel (Google + Bing) ---------------------------------

function fmtAgo(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function SitemapStatusPanel({
  status, loading, error, onRefresh,
}: {
  status: SitemapStatusReport | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  return (
    <div className="mt-6 rounded-lg border border-border bg-surface/30 p-4">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-widest text-ink-soft">
          Search Console status
        </div>
        <button onClick={onRefresh} disabled={loading} className="text-xs underline text-ink-soft hover:text-foreground disabled:opacity-50">
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      {!status && !error && <p className="mt-2 text-xs text-ink-soft">Loading status…</p>}
      {status && (
        <div className="mt-3 space-y-4">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
            <span><span className="text-ink-soft">Checked:</span> <span className="font-mono">{new Date(status.checkedAt).toLocaleString()}</span></span>
            <span><span className="text-ink-soft">URLs submitted:</span> <span className="font-mono">{status.totals.submitted}</span></span>
            <span><span className="text-ink-soft">Indexed:</span> <span className="font-mono">{status.totals.indexed}</span></span>
            <span><span className="text-ink-soft">Errors:</span> <span className={`font-mono ${status.totals.errors > 0 ? "text-red-600" : "text-emerald-600"}`}>{status.totals.errors}</span></span>
            <span><span className="text-ink-soft">Warnings:</span> <span className={`font-mono ${status.totals.warnings > 0 ? "text-amber-600" : "text-emerald-600"}`}>{status.totals.warnings}</span></span>
          </div>

          <div className="overflow-hidden rounded border border-border">
            <table className="w-full text-xs">
              <thead className="bg-surface text-left uppercase tracking-widest text-ink-soft">
                <tr>
                  <th className="px-3 py-2">Sitemap (Google)</th>
                  <th className="px-3 py-2">Submitted</th>
                  <th className="px-3 py-2">Indexed</th>
                  <th className="px-3 py-2">Errors</th>
                  <th className="px-3 py-2">Warnings</th>
                  <th className="px-3 py-2">Last downloaded</th>
                  <th className="px-3 py-2">Last submitted</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {status.google.sitemaps.map((s) => (
                  <tr key={s.path} className="border-t border-border align-top">
                    <td className="px-3 py-2 font-mono break-all">{s.path.replace("https://arifkhanglobal.com", "")}</td>
                    <td className="px-3 py-2 font-mono">{s.submitted ?? "—"}</td>
                    <td className="px-3 py-2 font-mono">{s.indexed ?? "—"}</td>
                    <td className={`px-3 py-2 font-mono ${(Number(s.errors) || 0) > 0 ? "text-red-600" : ""}`}>{s.errors ?? "—"}</td>
                    <td className={`px-3 py-2 font-mono ${(Number(s.warnings) || 0) > 0 ? "text-amber-600" : ""}`}>{s.warnings ?? "—"}</td>
                    <td className="px-3 py-2 font-mono">{fmtAgo(s.lastDownloaded)}</td>
                    <td className="px-3 py-2 font-mono">{fmtAgo(s.lastSubmitted)}</td>
                    <td className="px-3 py-2">
                      <span className={s.ok ? "text-emerald-600" : "text-red-600"}>
                        {s.registered ? (s.ok ? "✓ Healthy" : "✗ Issues") : "Not registered"}
                      </span>
                      {s.error && <div className="mt-1 text-[10px] text-red-600">{s.error}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded border border-border p-3 text-xs">
            <div className="font-semibold uppercase tracking-widest text-ink-soft mb-1">Bing (IndexNow)</div>
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              <span><span className="text-ink-soft">Key file:</span> <a href={status.bing.keyUrl} target="_blank" rel="noreferrer" className="font-mono underline hover:text-primary">{status.bing.keyUrl.replace("https://arifkhanglobal.com", "")}</a></span>
              <span><span className="text-ink-soft">Reachable:</span> <span className={`font-mono ${status.bing.keyReachable ? "text-emerald-600" : "text-red-600"}`}>{status.bing.keyReachable ? "yes" : "no"}</span></span>
              <span><span className="text-ink-soft">Key matches:</span> <span className={`font-mono ${status.bing.keyMatches ? "text-emerald-600" : "text-red-600"}`}>{status.bing.keyMatches ? "yes" : "no"}</span></span>
              <span><span className="text-ink-soft">HTTP:</span> <span className="font-mono">{status.bing.httpStatus}</span></span>
            </div>
            <p className="mt-2 text-ink-soft">{status.bing.note}</p>
          </div>

          <p className="text-[11px] text-ink-soft">
            CI after each deploy: <code className="font-mono">curl -X POST https://arifkhanglobal.com/api/admin/post-deploy-sitemaps</code>
          </p>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/admin/publish-check")({
  component: PublishCheck,
  head: () => ({
    meta: [
      { title: "Publish Verification | Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});
